import PropTypes from "prop-types";
import Popper from "@mui/material/Popper";
import { Grid } from "@mui/material";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import classNames from "classnames";
import AnimatedNumber from "../components/AnimatedNumber";
import { numberWithCommas } from "../utils";
import { getRewardColor } from "../utils/rewards";
import { roundTo } from "../utils/roundTo";
import css from "../styles_web/RewardsPopper.module.css";
import { useEffect, useRef } from "react";
import { media } from "../utils/media";
import { ASSETS_BASE_URL } from "../constants";
import Image from "../components/shared/Image";

const BorderLinearProgress = styled(LinearProgress)(({ theme, tier }) => ({
  height: 10,
  borderRadius: 5,
  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.grey[200],
  },
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 5,
    backgroundColor: `var(${getRewardColor(tier)})`,
    //backgroundColor: "var(--light-blue)",
  },
}));

const RewardsProgressDetails = ({
  tier,
  tierLabel,
  isLastTier,
  nextTier,
  nextTierLabel,
  tierProgressPercent,
  points,
  nextTierSpend,
  expiring,
  expiringPoints,
}) => {
  const getIcon = (tier, className) => {
    if (tier) {
      return (
        <Image
          className={classNames(
            [css["tier-icon"]],
            [css[`tier-icon-${tier}`]],
            className
          )}
          src={`${ASSETS_BASE_URL}/image/website/content/checkout/${tier.toLowerCase()}.svg`}
          alt={`tier-${tier}`}
        />
      );
    }
    return (
      <Image
        className={classNames(
          [css["tier-icon"]],
          [css[`tier-icon-${tier}`]],
          className
        )}
        src={media.checkout.bronzeTierImg}
        alt={`tier-${tier}`}
      />
    );
  };

  return (
    <Grid container display="flex" justifyContent="center">
      <Grid
        item
        container
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {getIcon(tier)}
        <p
          className={css["tier"]}
          style={{ color: `var(${getRewardColor(tier)})` }}
        >
          {tierLabel}
        </p>
      </Grid>
      <Grid
        item
        container
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <span className={css["points"]}>
          <AnimatedNumber number={points}></AnimatedNumber>
        </span>
      </Grid>

      {!isLastTier && (
        <Grid item container>
          <Grid item xs={2} className={css["tier-from"]}>
            <div
              className={css["tier-from-label"]}
              style={{ color: `var(${getRewardColor(tier)})` }}
            >
              {getIcon(tier)} {tierLabel}
            </div>
          </Grid>
          <Grid item xs={6} margin="auto">
            <BorderLinearProgress
              variant="determinate"
              tier={tier}
              value={tierProgressPercent}
            />
          </Grid>
          <Grid item xs={2} className={css["tier-to"]}>
            <div
              className={css["tier-to-label"]}
              style={{ color: `var(${getRewardColor(nextTier)})` }}
            >
              {getIcon(nextTier)} {nextTierLabel}
            </div>
          </Grid>
        </Grid>
      )}

      {isLastTier && (
        <Grid item container>
          <Grid item xs={2} className={css["tier-from"]}>
            {getIcon("stars")}
          </Grid>
          <Grid item xs={6} margin="auto">
            <div className={css["top-tier-text"]}>You Rule</div>
          </Grid>
          <Grid item xs={2} className={css["tier-to"]}>
            {getIcon("stars", css["rotate-90"])}
          </Grid>
        </Grid>
      )}

      {expiringPoints > 0 && (
        <Grid
          item
          container
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          <p className={css["expiring"]}>{expiring}</p>
        </Grid>
      )}

      <div className={css["divider"]}></div>
      {!isLastTier && (
        <>
          <Grid
            item
            container
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <div className={css["spend"]}>
              Spend ${numberWithCommas(roundTo(nextTierSpend, 2)?.toFixed(2))}
            </div>
          </Grid>
          <Grid
            item
            container
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <div className={css["to-reach"]}>to reach</div>
          </Grid>
          <Grid
            item
            container
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <div
              className={css["next-tier"]}
              style={{ color: `var(${getRewardColor(nextTier)})` }}
            >
              {nextTier}
            </div>
          </Grid>
        </>
      )}
      <Grid
        item
        container
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        <Link
          className={css["benefits-link"]}
          to="/b2b/my-account/regen-rewards"
          alt="Rewards"
        >
          See Benefits
        </Link>
      </Grid>
    </Grid>
  );
};

RewardsProgressDetails.propTypes = {
  tier: PropTypes.string,
  tierLabel: PropTypes.string,
  isFirstTier: PropTypes.bool,
  isLastTier: PropTypes.bool,
  points: PropTypes.number,
  nextTier: PropTypes.string,
  nextTierLabel: PropTypes.string,
  nextTierSpend: PropTypes.number,
  tierProgressPercent: PropTypes.number,
  expiring: PropTypes.string,
  expiringPoints: PropTypes.number,
};

const RewardsPopper = ({
  isOpen,
  tier,
  tierLabel,
  isLastTier,
  nextTier,
  nextTierLabel,
  nextTierSpend,
  tierProgressPercent,
  points,
  expiring,
  expiringPoints,
  onClosed,
  anchorEl,
}) => {
  const popperRef = useRef();
  useEffect(() => {
    function handleClickOutside(e) {
      if (
        popperRef.current &&
        !popperRef.current.contains(e.target) &&
        anchorEl &&
        !anchorEl.contains(e.target)
      ) {
        onClosed();
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    const resized = (e) => {
      const width =
        window.innerWidth ||
        document.documentElement.clientWidth ||
        document.body.clientWidth;
      //sized to mobile
      if (width < 1200) onClosed();
    };
    window.addEventListener("resize", resized);
    window.addEventListener("scroll", onClosed);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", resized);
      window.removeEventListener("scroll", onClosed);
    };
  }, [anchorEl, popperRef, onClosed]);

  return (
    <Popper
      ref={popperRef}
      placement="bottom-start"
      className={css["popper"]}
      open={isOpen}
      anchorEl={anchorEl}
    >
      <div>
        <div className={css["point"]}></div>
        <div className={css["content"]}>
          <RewardsProgressDetails
            tier={tier}
            tierLabel={tierLabel}
            isLastTier={isLastTier}
            nextTier={nextTier}
            nextTierLabel={nextTierLabel}
            nextTierSpend={nextTierSpend}
            tierProgressPercent={tierProgressPercent}
            points={points}
            expiring={expiring}
            expiringPoints={expiringPoints}
          ></RewardsProgressDetails>
        </div>
      </div>
    </Popper>
  );
};

RewardsPopper.propTypes = {
  isOpen: PropTypes.bool,
  tier: PropTypes.string,
  tierLabel: PropTypes.string,
  isLastTier: PropTypes.bool,
  points: PropTypes.number,
  nextTier: PropTypes.string,
  nextTierLabel: PropTypes.string,
  nextTierSpend: PropTypes.number,
  tierProgressPercent: PropTypes.number,
  info: PropTypes.string,
  expiring: PropTypes.string,
  expiringPoints: PropTypes.number,
  anchorEl: PropTypes.object,
  onClosed: PropTypes.func,
};

export default RewardsPopper;
